import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import Button from './Button';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message?: string;
  children?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  isProcessing?: boolean;
  processingText?: string;
}

export default function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  children,
  confirmText,
  cancelText,
  confirmVariant = 'primary',
  isProcessing = false,
  processingText,
}: ConfirmDialogProps) {
  const { t } = useTranslation();

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/50"
        />

        {/* Dialog */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6 z-10"
        >
          {/* Title */}
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{title}</h3>

          {/* Message or Children */}
          <div className="mb-6">
            {message && (
              <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                {message}
              </p>
            )}
            {children}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3">
            <Button variant="ghost" onClick={onClose} disabled={isProcessing}>
              {cancelText || t('common.cancel')}
            </Button>
            <Button variant={confirmVariant} onClick={onConfirm} disabled={isProcessing}>
              {isProcessing
                ? processingText || t('common.loading')
                : confirmText || t('common.confirm')}
            </Button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
